/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

/*
* The following modules are intentionally not exported: tools
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace string
*/
var string = {};

/**
* @name acronym
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/acronym}
*/
import acronym from './../acronym';
setReadOnly( string, 'acronym', acronym );

/**
* @name base
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/base}
*/
import base from './../base';
setReadOnly( string, 'base', base );

/**
* @name camelcase
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/camelcase}
*/
import camelcase from './../camelcase';
setReadOnly( string, 'camelcase', camelcase );

/**
* @name capitalize
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/capitalize}
*/
import capitalize from './../capitalize';
setReadOnly( string, 'capitalize', capitalize );

/**
* @name codePointAt
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/code-point-at}
*/
import codePointAt from './../code-point-at';
setReadOnly( string, 'codePointAt', codePointAt );

/**
* @name constantcase
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/constantcase}
*/
import constantcase from './../constantcase';
setReadOnly( string, 'constantcase', constantcase );

/**
* @name dotcase
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/dotcase}
*/
import dotcase from './../dotcase';
setReadOnly( string, 'dotcase', dotcase );

/**
* @name endsWith
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/ends-with}
*/
import endsWith from './../ends-with';
setReadOnly( string, 'endsWith', endsWith );

/**
* @name first
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/first}
*/
import first from './../first';
setReadOnly( string, 'first', first );

/**
* @name forEach
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/for-each}
*/
import forEach from './../for-each';
setReadOnly( string, 'forEach', forEach );

/**
* @name format
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/format}
*/
import format from './../format';
setReadOnly( string, 'format', format );

/**
* @name fromCodePoint
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/from-code-point}
*/
import fromCodePoint from './../from-code-point';
setReadOnly( string, 'fromCodePoint', fromCodePoint );

/**
* @name headercase
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/headercase}
*/
import headercase from './../headercase';
setReadOnly( string, 'headercase', headercase );

/**
* @name kebabcase
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/kebabcase}
*/
import kebabcase from './../kebabcase';
setReadOnly( string, 'kebabcase', kebabcase );

/**
* @name last
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/last}
*/
import last from './../last';
setReadOnly( string, 'last', last );

/**
* @name lpad
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/left-pad}
*/
import lpad from './../left-pad';
setReadOnly( string, 'lpad', lpad );

/**
* @name ltrim
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/left-trim}
*/
import ltrim from './../left-trim';
setReadOnly( string, 'ltrim', ltrim );

/**
* @name ltrimN
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/left-trim-n}
*/
import ltrimN from './../left-trim-n';
setReadOnly( string, 'ltrimN', ltrimN );

/**
* @name lowercase
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/lowercase}
*/
import lowercase from './../lowercase';
setReadOnly( string, 'lowercase', lowercase );

/**
* @name nextCodePointIndex
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/next-code-point-index}
*/
import nextCodePointIndex from './../next-code-point-index';
setReadOnly( string, 'nextCodePointIndex', nextCodePointIndex );

/**
* @name nextGraphemeClusterBreak
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/next-grapheme-cluster-break}
*/
import nextGraphemeClusterBreak from './../next-grapheme-cluster-break';
setReadOnly( string, 'nextGraphemeClusterBreak', nextGraphemeClusterBreak );

/**
* @name numCodePoints
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/num-code-points}
*/
import numCodePoints from './../num-code-points';
setReadOnly( string, 'numCodePoints', numCodePoints );

/**
* @name numGraphemeClusters
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/num-grapheme-clusters}
*/
import numGraphemeClusters from './../num-grapheme-clusters';
setReadOnly( string, 'numGraphemeClusters', numGraphemeClusters );

/**
* @name num2words
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/num2words}
*/
import num2words from './../num2words';
setReadOnly( string, 'num2words', num2words );

/**
* @name pad
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/pad}
*/
import pad from './../pad';
setReadOnly( string, 'pad', pad );

/**
* @name pascalcase
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/pascalcase}
*/
import pascalcase from './../pascalcase';
setReadOnly( string, 'pascalcase', pascalcase );

/**
* @name percentEncode
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/percent-encode}
*/
import percentEncode from './../percent-encode';
setReadOnly( string, 'percentEncode', percentEncode );

/**
* @name prevGraphemeClusterBreak
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/prev-grapheme-cluster-break}
*/
import prevGraphemeClusterBreak from './../prev-grapheme-cluster-break';
setReadOnly( string, 'prevGraphemeClusterBreak', prevGraphemeClusterBreak );

/**
* @name removeFirst
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/remove-first}
*/
import removeFirst from './../remove-first';
setReadOnly( string, 'removeFirst', removeFirst );

/**
* @name removeLast
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/remove-last}
*/
import removeLast from './../remove-last';
setReadOnly( string, 'removeLast', removeLast );

/**
* @name removePunctuation
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/remove-punctuation}
*/
import removePunctuation from './../remove-punctuation';
setReadOnly( string, 'removePunctuation', removePunctuation );

/**
* @name removeUTF8BOM
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/remove-utf8-bom}
*/
import removeUTF8BOM from './../remove-utf8-bom';
setReadOnly( string, 'removeUTF8BOM', removeUTF8BOM );

/**
* @name removeWords
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/remove-words}
*/
import removeWords from './../remove-words';
setReadOnly( string, 'removeWords', removeWords );

/**
* @name repeat
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/repeat}
*/
import repeat from './../repeat';
setReadOnly( string, 'repeat', repeat );

/**
* @name replace
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/replace}
*/
import replace from './../replace';
setReadOnly( string, 'replace', replace );

/**
* @name replaceBefore
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/replace-before}
*/
import replaceBefore from './../replace-before';
setReadOnly( string, 'replaceBefore', replaceBefore );

/**
* @name reverse
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/reverse}
*/
import reverse from './../reverse';
setReadOnly( string, 'reverse', reverse );

/**
* @name rpad
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/right-pad}
*/
import rpad from './../right-pad';
setReadOnly( string, 'rpad', rpad );

/**
* @name rtrim
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/right-trim}
*/
import rtrim from './../right-trim';
setReadOnly( string, 'rtrim', rtrim );

/**
* @name rtrimN
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/right-trim-n}
*/
import rtrimN from './../right-trim-n';
setReadOnly( string, 'rtrimN', rtrimN );

/**
* @name snakecase
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/snakecase}
*/
import snakecase from './../snakecase';
setReadOnly( string, 'snakecase', snakecase );

/**
* @name splitGraphemeClusters
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/split-grapheme-clusters}
*/
import splitGraphemeClusters from './../split-grapheme-clusters';
setReadOnly( string, 'splitGraphemeClusters', splitGraphemeClusters );

/**
* @name startcase
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/startcase}
*/
import startcase from './../startcase';
setReadOnly( string, 'startcase', startcase );

/**
* @name startsWith
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/starts-with}
*/
import startsWith from './../starts-with';
setReadOnly( string, 'startsWith', startsWith );

/**
* @name substringAfter
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/substring-after}
*/
import substringAfter from './../substring-after';
setReadOnly( string, 'substringAfter', substringAfter );

/**
* @name substringAfterLast
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/substring-after-last}
*/
import substringAfterLast from './../substring-after-last';
setReadOnly( string, 'substringAfterLast', substringAfterLast );

/**
* @name substringBefore
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/substring-before}
*/
import substringBefore from './../substring-before';
setReadOnly( string, 'substringBefore', substringBefore );

/**
* @name substringBeforeLast
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/substring-before-last}
*/
import substringBeforeLast from './../substring-before-last';
setReadOnly( string, 'substringBeforeLast', substringBeforeLast );

/**
* @name graphemeClusters2iterator
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/to-grapheme-cluster-iterator}
*/
import graphemeClusters2iterator from './../to-grapheme-cluster-iterator';
setReadOnly( string, 'graphemeClusters2iterator', graphemeClusters2iterator );

/**
* @name graphemeClusters2iteratorRight
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/to-grapheme-cluster-iterator-right}
*/
import graphemeClusters2iteratorRight from './../to-grapheme-cluster-iterator-right';
setReadOnly( string, 'graphemeClusters2iteratorRight', graphemeClusters2iteratorRight );

/**
* @name toWellFormed
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/to-well-formed}
*/
import toWellFormed from './../to-well-formed';
setReadOnly( string, 'toWellFormed', toWellFormed );

/**
* @name trim
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/trim}
*/
import trim from './../trim';
setReadOnly( string, 'trim', trim );

/**
* @name truncate
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/truncate}
*/
import truncate from './../truncate';
setReadOnly( string, 'truncate', truncate );

/**
* @name truncateMiddle
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/truncate-middle}
*/
import truncateMiddle from './../truncate-middle';
setReadOnly( string, 'truncateMiddle', truncateMiddle );

/**
* @name uncapitalize
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/uncapitalize}
*/
import uncapitalize from './../uncapitalize';
setReadOnly( string, 'uncapitalize', uncapitalize );

/**
* @name uppercase
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/uppercase}
*/
import uppercase from './../uppercase';
setReadOnly( string, 'uppercase', uppercase );

/**
* @name utf16ToUTF8Array
* @memberof string
* @readonly
* @type {Function}
* @see {@link module:@stdlib/string/utf16-to-utf8-array}
*/
import utf16ToUTF8Array from './../utf16-to-utf8-array';
setReadOnly( string, 'utf16ToUTF8Array', utf16ToUTF8Array );


// EXPORTS //

export default string;
